import { Message as MessageType } from '@kifuerpflege/shared';
import { cn } from '@/lib/utils';
import { User, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { useState } from 'react';
import { copyToClipboard } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MessageProps {
  message: MessageType;
}

export function Message({ message }: MessageProps) {
  const [copied, setCopied] = useState(false);

  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  const handleCopy = async () => {
    const success = await copyToClipboard(message.content);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className={cn(
        'flex gap-3 message-enter',
        isUser && 'flex-row-reverse'
      )}
    >
      {/* Avatar */}
      {isUser ? (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <User className="h-4 w-4" />
        </div>
      ) : (
        <img
          src="/logo-chat.png"
          alt="KI"
          className="h-8 w-8 shrink-0 rounded-full object-cover"
        />
      )}

      {/* Message Content */}
      <div className={cn('flex-1 space-y-2 overflow-hidden', isUser && 'flex flex-col items-end')}>
        {/* Message Bubble */}
        <div
          className={cn(
            'inline-block max-w-[80%] rounded-lg px-4 py-2',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-transparent',
            !isUser && 'group relative'
          )}
        >
          {/* Copy Button (only for assistant messages) */}
          {isAssistant && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute -right-2 top-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          )}

          {/* Message Text */}
          {isUser ? (
            <p className="whitespace-pre-wrap break-words text-sm">
              {message.content}
            </p>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none break-words">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Timestamp */}
        <p className={cn(
          'text-xs text-muted-foreground px-1',
          isUser && 'text-right'
        )}>
          {new Date(message.createdAt).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}
